/**
 * BadgeCelebrationsContext — Phase MP-9C
 *
 * Single global instance of the badge celebration controller.
 * Renders the celebration modal once at app root and exposes a
 * `celebrate(badges)` function that any component (poll vote button,
 * discussion form, mock-draft submit, money-pot approval, …) can call
 * with the `newly_awarded_badges` array from its API response.
 *
 * Backwards compatible — components can still call useBadgeCelebrations()
 * directly to get the per-instance hook, but the new pattern is:
 *
 *   import { useBadgeCelebrate } from '../contexts/BadgeCelebrationsContext';
 *   const celebrate = useBadgeCelebrate();
 *   const res = await pollsApi.vote(...);
 *   celebrate(res.data?.newly_awarded_badges);
 */
import React, { createContext, useContext } from 'react';
import useBadgeCelebrations from '../hooks/useBadgeCelebrations';
import BadgeCelebrationModal from '../components/member/BadgeCelebrationModal';

const BadgeCelebrationsCtx = createContext(null);

export const BadgeCelebrationsProvider = ({ children }) => {
  const ctrl = useBadgeCelebrations();
  return (
    <BadgeCelebrationsCtx.Provider value={ctrl}>
      {children}
      <BadgeCelebrationModal
        badges={ctrl.queue}
        open={ctrl.open}
        onClose={ctrl.close}
      />
    </BadgeCelebrationsCtx.Provider>
  );
};

export const useBadgeCelebrationCtx = () => useContext(BadgeCelebrationsCtx);

// Convenience — return ONLY the celebrate function so call sites stay
// terse: `const celebrate = useBadgeCelebrate(); celebrate(badges);`
export const useBadgeCelebrate = () => {
  const ctx = useContext(BadgeCelebrationsCtx);
  return (badges) => {
    if (!ctx || !badges || !badges.length) return;
    ctx.ingestViews(badges);
  };
};
